import {
  Component,
  ElementRef,
  viewChild,
  signal,
  OnDestroy,
  AfterViewInit,
  Inject,
  PLATFORM_ID,
  NgZone,
  OnInit,
  ChangeDetectorRef,
  inject
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as THREE from 'three';
import { gsap } from 'gsap';
import { PropertyService } from '../../services/property.service';

export interface PropertyMarker {
  id: string;
  title: string;
  imageUrl: string;
  link: string;
  lat: number;
  lon: number;
  displayLat?: number;
  displayLon?: number;
  visualIndex?: number;
  screenX?: number;
  screenY?: number;
  markerX?: number;
  markerY?: number;
  visible?: boolean;
  anchor?: THREE.Object3D;
  isDocked?: boolean;
}

export interface FeaturedPropertyDto {
  featuredItemId: number;
  id: string;
  title: string;
  imageUrl: string;
  link: string;
  lat: number;
  lon: number;
  displayLat?: number;
  displayLon?: number;
  visualIndex?: number;
  screenX?: number;
  screenY?: number;
  markerX?: number;
  markerY?: number;
  visible?: boolean;
  anchor?: THREE.Object3D;
  isDocked?: boolean;
}

@Component({
  selector: 'app-featured-globe',
  standalone: true,
  templateUrl: './featured-globe.component.html',
  styleUrl: './featured-globe.component.scss'
})
export class FeaturedGlobeComponent implements OnInit, AfterViewInit, OnDestroy {
  private propertyService = inject(PropertyService);
  private canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('globeCanvas');
  public visibleProperties = signal<PropertyMarker[]>([]);

  public canvasWidth = 1000;
  public canvasHeight = 800;

  private propertiesData: PropertyMarker[] = [
    // { id: 'e1506ad7-c039-4f17-a3e7-62b8c1e44ce1', title: 'Gulf Access in Cape Coral', imageUrl: 'https://horizinns-property-images.s3.amazonaws.com/properties/e1506ad7-c039-4f17-a3e7-62b8c1e44ce1/1761057637332.jpg', link: '/property/e1506ad7-c039-4f17-a3e7-62b8c1e44ce1', lat: 26.5629, lon: -81.9495 },
    // { id: 'b96edfaf-da11-4ef3-b9fc-8658614a8951', title: 'Year Long in Venice, FL', imageUrl: 'https://horizinns-property-images.s3.amazonaws.com/properties/b96edfaf-da11-4ef3-b9fc-8658614a8951/1775922647595.jpg', link: '/property/b96edfaf-da11-4ef3-b9fc-8658614a8951', lat: 27.0998, lon: -82.4543 },
    // { id: '288b36cd-8fc9-4400-8b61-34fccb62cb35', title: 'Aubuchon Gulf-access', imageUrl: 'https://horizinns-property-images.s3.amazonaws.com/properties/288b36cd-8fc9-4400-8b61-34fccb62cb35/1761426620553.jpg', link: '/property/288b36cd-8fc9-4400-8b61-34fccb62cb35', lat: 26.6395, lon: -81.9724 }
  ];

  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private globe!: THREE.Mesh;
  private atmosphere!: THREE.Mesh;

  private animationFrameId!: number;
  private globeRadius = 10;
  private isMobile = false;
  private hasScrolledIntoView = false;
  private blurState = { val: 0 };

  // Resize handling
  private resizeObserver!: ResizeObserver;
  private boundWindowResize!: () => void;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    // Magnetic spread for markers
    const sortedForSpread = [...this.propertiesData].sort((a, b) => a.lon - b.lon);
    this.propertiesData.forEach(prop => {
      const spreadIndex = sortedForSpread.findIndex(p => p.id === prop.id);
      const centerIndex = (this.propertiesData.length - 1) / 2;
      const spreadOffset = (spreadIndex - centerIndex) * 6.5;

      prop.displayLon = prop.lon + spreadOffset;
      prop.displayLat = prop.lat + (spreadIndex % 2 === 0 ? -1.5 : 2.0);
    });
  }

  ngOnInit() {
    this.loadFeaturedProperties();
  }

  loadFeaturedProperties() {
    this.propertyService.getFeaturedItems().subscribe({
      next: (data: FeaturedPropertyDto[]) => {
        console.log('Featured properties loaded:', data);
        this.propertiesData = data.map(dto => this.mapToPropertyMarker(dto));
        this.applyMagneticSpread();

        const sortedForSpread = [...this.propertiesData].sort((a, b) => a.lon - b.lon);
        this.propertiesData.forEach(prop => {
          console.log('loadprop sorting and display lat long', prop);
          const spreadIndex = sortedForSpread.findIndex(p => p.id === prop.id);
          const centerIndex = (this.propertiesData.length - 1) / 2;
          const spreadOffset = (spreadIndex - centerIndex) * 1.05;

          prop.displayLon = prop.lon + spreadOffset;
          prop.displayLat = prop.lat + (spreadIndex % 2 === 0 ? -0.8 : 1.05);
        });

        // Map and store data
        console.log('Featured Property data', this.propertiesData);

        // Create 3D markers on the globe
        if (this.globe) {
          this.createMarkers();
        }

        // Update positions so cards can appear
        this.updateMarkerPositions();

        // If user already scrolled past the section, trigger docking manually
        if (this.hasScrolledIntoView && this.propertiesData.length > 0) {
          const halfWidth = this.canvasWidth / 2;
          this.propertiesData.forEach((prop, i) => {
            prop.visualIndex = i;
            if (!prop.isDocked) {
              this.triggerDockSequence(prop, halfWidth);
            }
          });
        }
      },
      error: (err) => {
        console.error('Failed to load featured properties', err);
      }
    });
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.initThreeJS();
          this.setupScrollTrigger();
          this.animate();
        }, 80);
      });
    }
  }

  private initThreeJS(): void {
    const canvas = this.canvasRef().nativeElement;
    const container = canvas.parentElement as HTMLElement;
    this.isMobile = window.innerWidth < 768;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(48, container.clientWidth / container.clientHeight, 0.1, 1000);
    this.camera.position.set(0, 6, 26);

    this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // === RESIZE HANDLING ===
    this.resizeObserver = new ResizeObserver(() => this.onWindowResize());
    this.resizeObserver.observe(container);

    this.boundWindowResize = () => this.onWindowResize();
    window.addEventListener('resize', this.boundWindowResize);
    // =========================

    this.scene.add(new THREE.AmbientLight(0xffffff, 0.95));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.4);
    dirLight.position.set(8, 12, 6);
    this.scene.add(dirLight);

    this.createGlobe();
    this.createMarkers();
  }

  private createGlobe(): void {
    const geometry = new THREE.SphereGeometry(this.globeRadius, 64, 64);
    const material = new THREE.MeshPhongMaterial({
      color: 0x5eb0ff, shininess: 35, specular: 0xa0e0ff, emissive: 0x113355
    });

    const loader = new THREE.TextureLoader();
    const urls = [
      'assets/images/globe/2k_earth_daymap.jpg',
      'assets/images/globe/earth_atmos_2048.jpg'
    ];

    urls.forEach(url => {
      loader.load(url, (tex) => {
        material.map = tex;
        material.needsUpdate = true;
      });
    });

    this.globe = new THREE.Mesh(geometry, material);
    this.globe.rotation.y = -Math.PI;
    this.scene.add(this.globe);

    const atmGeo = new THREE.SphereGeometry(this.globeRadius * 1.15, 64, 64);
    const atmMat = new THREE.ShaderMaterial({
      uniforms: { glowColor: { value: new THREE.Color(0x88eeff) }, intensity: { value: 1.2 } },
      vertexShader: `varying vec3 vNormal; varying vec3 vPos; void main() { vNormal = normalize(normalMatrix * normal); vPos = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
      fragmentShader: `
        uniform vec3 glowColor; 
        uniform float intensity; 
        varying vec3 vNormal; 
        varying vec3 vPos; 
        void main() { 
          vec3 viewDir = normalize(cameraPosition - vPos); 
          float ndotv = max(0.0, dot(viewDir, vNormal));
          float glow = pow(1.0 - ndotv, 2.5) * intensity; 
          float edgeFade = smoothstep(0.0, 0.2, ndotv); 
          gl_FragColor = vec4(glowColor, glow * 0.5 * edgeFade); 
        }`,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false
    });

    this.atmosphere = new THREE.Mesh(atmGeo, atmMat);
    this.scene.add(this.atmosphere);
  }

  private createMarkers(): void {
    // Clear existing markers first (in case of reload)
    // Optional: 
    //this.globe.children.forEach(child => this.globe.remove(child));
    console.log('createMarkers');
    this.propertiesData.forEach(prop => {
      //if (!prop.displayLat || !prop.displayLon) return;
      console.log('prop', prop);
      const pos = this.latLonToVector3(prop.displayLat ?? 0, prop.displayLon ?? 0, this.globeRadius + 0.6);

      const marker = new THREE.Mesh(
        new THREE.SphereGeometry(0.25, 12, 12),
        new THREE.MeshPhongMaterial({ 
          color: 0xff3366, 
          emissive: 0xff1144, 
          shininess: 100, 
          emissiveIntensity: 1.2 
        })
      );

      const glowRing = new THREE.Mesh(
        new THREE.SphereGeometry(0.58, 16, 16),
        new THREE.MeshBasicMaterial({ 
          color: 0xff5588, 
          transparent: true, 
          opacity: 0.3, 
          side: THREE.BackSide 
        })
      );

      const markerGroup = new THREE.Group();
      markerGroup.position.copy(pos);
      markerGroup.add(marker, glowRing);
      this.globe.add(markerGroup);

      prop.anchor = markerGroup;
      prop.isDocked = false;
    });
  }
  
  private longitudeOffsetDeg = 0;
  private latLonToVector3(lat: number, lon: number, radius: number): THREE.Vector3 {
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lon + 180 + this.longitudeOffsetDeg) * (Math.PI / 180);
    return new THREE.Vector3(
      -(radius * Math.sin(phi) * Math.cos(theta)),
      radius * Math.cos(phi),
      radius * Math.sin(phi) * Math.sin(theta)
    );
  }

  private setupScrollTrigger(): void {
    const section = this.canvasRef().nativeElement.closest('.globe-section') as HTMLElement;
    if (!section) return;

    new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !this.hasScrolledIntoView) {
        this.hasScrolledIntoView = true;
        const canvasEl = this.canvasRef().nativeElement;

        gsap.to(this.blurState, {
          val: 6, duration: 1.2, ease: "power2.in",
          onUpdate: () => canvasEl.style.filter = `blur(${this.blurState.val}px)`
        });
        gsap.to(this.blurState, {
          val: 0, duration: 1.5, delay: 1.5, ease: "power2.out",
          onUpdate: () => canvasEl.style.filter = `blur(${this.blurState.val}px)`
        });

        gsap.to(this.globe.rotation, {
          y: Math.PI * 4,
          duration: 3.5,
          ease: "power3.inOut",
          onComplete: () => {
            this.updateMarkerPositions();

            const sortedByScreenX = [...this.propertiesData]
              .filter(p => p.markerX !== undefined)
              .sort((a, b) => (a.markerX! - b.markerX!));

            sortedByScreenX.forEach((prop, i) => {
              prop.visualIndex = i;
              if (!prop.isDocked) this.triggerDockSequence(prop, this.canvasWidth / 2);
            });
          }
        });
      }
    }, { threshold: 0.35 }).observe(section);
  }

  private animate = (): void => {
    this.animationFrameId = requestAnimationFrame(this.animate);
    if (this.atmosphere) this.atmosphere.rotation.y = this.globe.rotation.y;
    this.updateMarkerPositions();
    this.renderer.render(this.scene, this.camera);
  };

  private onWindowResize(): void {
    const canvas = this.canvasRef().nativeElement;
    if (!canvas) return;

    const container = canvas.parentElement as HTMLElement;
    if (!container || container.clientWidth === 0 || container.clientHeight === 0) return;

    this.camera.aspect = container.clientWidth / container.clientHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(container.clientWidth, container.clientHeight);

    this.isMobile = window.innerWidth < 768;

    this.updateMarkerPositions();

    if (this.hasScrolledIntoView) {
      this.repositionDockedCards();
    }
  }

  private getDockPosition(prop: PropertyMarker, halfWidth: number): { dockX: number; dockY: number } {
    let dockX = halfWidth;
    let dockY = 160;

    if (this.isMobile) {
      // === MOBILE: Alternating left/right zigzag ===
      const isRightSide = (prop.visualIndex! % 2 === 0); // 0 = right, 1 = left, 2 = right

      dockX = isRightSide 
        ? halfWidth + 90     // Right side
        : halfWidth - 90;    // Left side

      dockY = 60 + (prop.visualIndex! * 115);
    } 
    else {
      // === DESKTOP: Horizontal centered row ===
      const spacing = 360;
      const startX = halfWidth - (spacing * (this.propertiesData.length - 1) / 2);
      dockX = startX + (prop.visualIndex! * spacing);
      dockY = 300;
    }

    return { dockX, dockY };
  }

  private repositionDockedCards(): void {
    const canvas = this.renderer.domElement;
    const halfWidth = canvas.clientWidth / 2;

    this.propertiesData.forEach((prop) => {
      if (!prop.isDocked || prop.visualIndex === undefined) return;

      const { dockX, dockY } = this.getDockPosition(prop, halfWidth);
      gsap.set(prop, { screenX: dockX, screenY: dockY });
    });

    this.visibleProperties.set([...this.propertiesData]);
  }

  private updateMarkerPositions(): void {
    if (!this.renderer) return;
    const updatedMarkers: PropertyMarker[] = [];
    const canvas = this.renderer.domElement;

    this.canvasWidth = canvas.clientWidth;
    this.canvasHeight = canvas.clientHeight;

    const halfWidth = this.canvasWidth / 2;
    const halfHeight = this.canvasHeight / 2;

    this.globe.updateMatrixWorld();

    this.propertiesData.forEach((prop) => {
      if (!prop.anchor) return;

      const position = new THREE.Vector3();
      position.setFromMatrixPosition(prop.anchor.matrixWorld);

      const isFrontOfGlobe = position.z > 0;
      position.project(this.camera);

      prop.markerX = (position.x * halfWidth) + halfWidth;
      prop.markerY = -(position.y * halfHeight) + halfHeight;

      if (isFrontOfGlobe || prop.isDocked) {
        prop.visible = true;
        updatedMarkers.push(prop);

        if (!prop.isDocked) {
          prop.screenX = prop.markerX;
          prop.screenY = prop.markerY;
        }
      }
    });

    this.visibleProperties.set(updatedMarkers);
  }

  get cardVisualHeight(): number {
    return this.isMobile ? 175 : 250;
  }

  private triggerDockSequence(prop: PropertyMarker, halfWidth: number): void {
    prop.isDocked = true;

    const markerMesh = prop.anchor!.children[0];
    const glowRing = prop.anchor!.children[1];

    gsap.to(markerMesh.scale, { x: 0.3, y: 0.3, z: 0.3, duration: 0.5, ease: "power2.out" });
    gsap.to(glowRing.scale, { x: 0, y: 0, z: 0, duration: 0.3 });

    const { dockX, dockY } = this.getDockPosition(prop, halfWidth);

    gsap.to(prop, { screenX: dockX, screenY: dockY, duration: 1.2, ease: "power3.out" });

    setTimeout(() => {
      const wrapper = document.getElementById('marker-wrapper-' + prop.id);
      const card = wrapper?.querySelector('.property-card');
      if (card) {
        gsap.fromTo(card,
          { opacity: 0, scale: 0, xPercent: -50, yPercent: -100 },
          { opacity: 1, scale: 1, xPercent: -50, yPercent: -100, duration: 0.8, ease: "back.out(1.2)" }
        );
      }
    }, 50);
  }
  
  mapToPropertyMarker(dto: FeaturedPropertyDto): PropertyMarker {
    
    return {
      id: dto.id,
      title: dto.title,
      imageUrl: dto.imageUrl,
      link: dto.link || `/property/${dto.id}`,
      lat: dto.lat,
      lon: dto.lon,

      // These are added on the frontend
      displayLat: undefined,
      displayLon: undefined,
      visualIndex: undefined,
      screenX: undefined,
      screenY: undefined,
      markerX: undefined,
      markerY: undefined,
      visible: false,
      anchor: undefined,
      isDocked: false
    };
  }

  private applyMagneticSpread(): void {
    if (this.propertiesData.length <= 1) return;

    const sorted = [...this.propertiesData].sort((a, b) => a.lon - b.lon);
    const lons = sorted.map(p => p.lon);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    const lonRange = maxLon - minLon;

    // Only spread if they're actually close together
    const baseSpread = lonRange < 8 ? 2.2 : 0.8;   // tighter spread when clustered

    const centerIndex = (sorted.length - 1) / 2;

    sorted.forEach((prop, i) => {
      const spreadIndex = i;
      const spreadOffset = (spreadIndex - centerIndex) * baseSpread;

      prop.displayLon = prop.lon + spreadOffset;
      prop.displayLat = prop.lat + (spreadIndex % 2 === 0 ? -0.9 : 1.1);
    });
  }

  ngOnDestroy(): void {
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
    if (this.renderer) this.renderer.dispose();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.boundWindowResize) {
      window.removeEventListener('resize', this.boundWindowResize);
    }
  }
}